import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, MessageCircle, Gift, Mail, Loader2, Check, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { trackCta, trackEvent } from '../hooks/useAnalytics';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';

const STORAGE_KEY = 'mbap_exit_intent_shown';
const WHATSAPP_NUMBER = '447448574616';

/**
 * Exit-intent retention modal.
 *
 * Triggers:
 *   • Desktop: mouse moves above the viewport (classic exit intent)
 *   • Mobile: user scrolls back up past 30% of the page after first
 *     scrolling at least 50% down — proxy for "thumb heading for back".
 *   • OR — 35 s on page without a CTA click as a fallback.
 *
 * Shown at most once per session (sessionStorage flag).
 *
 * If `preview` + `bizName` are provided (i.e. the visitor just had an AI ad
 * generated for them on the landing hero), the modal pivots to a
 * personalised email-capture: "Your AI ad is ready — drop your email to
 * keep it". This is by far the highest-converting variant because the
 * visitor has already seen the value before being asked for an email.
 *
 * If no preview is available, falls back to the original "Start free
 * trial / WhatsApp" two-button modal.
 */
const ExitIntentModal = ({ preview = null, bizName = '' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const markShown = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
  };

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
    } catch { /* ignore */ }

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    let maxScroll = 0;
    let triggered = false;

    const trigger = (label) => {
      if (triggered) return;
      triggered = true;
      markShown();
      setOpen(true);
      trackEvent('exit_intent_shown', { label, hasPreview: !!preview });
    };

    const onMouseOut = (e) => {
      if (isMobile) return;
      if (e.clientY <= 0 && !e.relatedTarget) trigger('mouseout_top');
    };
    const onScroll = () => {
      const dh = document.documentElement;
      const pct = (window.scrollY + window.innerHeight) / dh.scrollHeight;
      if (pct > maxScroll) maxScroll = pct;
      if (isMobile && maxScroll > 0.5 && pct < maxScroll - 0.2) {
        trigger('scroll_up_after_deep');
      }
    };
    const t = isMobile ? setTimeout(() => trigger('mobile_timer_35s'), 35_000) : null;

    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
      if (t) clearTimeout(t);
    };
  }, [preview]);

  if (!open) return null;

  const dismiss = (action) => {
    setOpen(false);
    trackEvent('exit_intent_action', { label: action, hasPreview: !!preview });
  };

  const goSignup = () => {
    trackCta('exit_intent_signup');
    dismiss('signup');
    navigate('/signup');
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !email.includes('@')) {
      toast({ title: 'Enter a valid email' });
      return;
    }
    setSending(true);
    trackEvent('exit_intent_lead_submit', { hasPreview: !!preview });
    try {
      await api.post('/auth/magic-link', {
        email: email.trim().toLowerCase(),
        name: bizName?.trim() || '',
        previewId: preview?.previewId || '',
        source: 'exit_intent',
      });
      setSent(true);
      trackEvent('exit_intent_lead_captured', { hasPreview: !!preview });
    } catch (err) {
      toast({ title: 'Could not send link', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSending(false);
    }
  };

  const greeting = "Hi! I was just looking at MyBusinessAdPro — could you tell me more?";
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(greeting)}`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => dismiss('backdrop')}
      data-testid="exit-intent-backdrop"
    >
      <div
        className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl p-7 sm:p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-modal"
      >
        <button
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-100 text-slate-500"
          onClick={() => dismiss('close')}
          aria-label="Close"
          data-testid="exit-intent-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* PERSONALISED VARIANT — visitor already generated a preview ad */}
        {preview && !sent && (
          <div data-testid="exit-intent-personalised">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h3 className="mt-4 text-2xl font-extrabold text-slate-900 tracking-tight">
              Wait — your AI ad is ready ✨
            </h3>
            <p className="mt-2 text-slate-600 text-sm leading-relaxed">
              Don&apos;t lose it. Drop your email and we&apos;ll send the full ad — copy, image and hashtags — straight to your inbox. <strong className="text-slate-900">No card. No spam.</strong>
            </p>

            {/* The actual headline they just generated — proof we have something for them */}
            <div className="mt-4 rounded-xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-rose-50 p-4">
              <div className="text-[10px] uppercase tracking-wider text-orange-600 font-bold">Your draft headline</div>
              <div className="mt-1 font-extrabold text-slate-900 leading-snug" data-testid="exit-intent-headline">
                {preview.headline}
              </div>
            </div>

            <form onSubmit={submitEmail} className="mt-5 space-y-2" data-testid="exit-intent-form">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.co.uk"
                  autoFocus
                  className="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="exit-intent-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-bold rounded-xl shadow-lg"
                data-testid="exit-intent-email-submit"
              >
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <>Email me my ad &amp; sign-in link</>}
              </Button>
              <p className="text-xs text-slate-500 flex items-center gap-1.5 justify-center pt-1">
                <Lock className="w-3 h-3" /> 7-day free trial · cancel any time · no card
              </p>
            </form>
          </div>
        )}

        {/* SUCCESS — email captured */}
        {preview && sent && (
          <div data-testid="exit-intent-sent">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center">
              <Check className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <h3 className="mt-4 text-2xl font-extrabold text-slate-900 text-center">Check your inbox 📥</h3>
            <p className="mt-2 text-slate-600 text-sm text-center">
              We&apos;ve sent your AI ad and a one-click sign-in link to <b>{email}</b>. The link works for 15 minutes.
            </p>
            <Button onClick={() => dismiss('sent_close')} className="mt-5 w-full h-11 bg-slate-900 text-white font-semibold rounded-xl">
              Got it
            </Button>
          </div>
        )}

        {/* FALLBACK — no preview, original two-button flow */}
        {!preview && (
          <div data-testid="exit-intent-fallback">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <h3 className="mt-4 text-2xl font-extrabold text-slate-900 tracking-tight">
              Before you go — try us free for 7 days
            </h3>
            <p className="mt-2 text-slate-600 text-sm leading-relaxed">
              Generate your first AI ad, image and caption in 30 seconds. <strong className="text-slate-900">Cancel any time and you&apos;re not charged a penny.</strong>
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /><span>AI copy <em>and</em> image — Meta-ready in seconds</span></li>
              <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /><span>UK ASA-compliant content, every time</span></li>
              <li className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" /><span>Cancel from your dashboard in two clicks</span></li>
            </ul>
            <div className="mt-6 flex flex-col gap-2">
              <Button
                onClick={goSignup}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl"
                data-testid="exit-intent-signup-btn"
              >
                Start my 7-day free trial
              </Button>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackCta('exit_intent_whatsapp'); dismiss('whatsapp'); }}
                className="w-full h-11 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold rounded-xl"
                data-testid="exit-intent-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> Or chat with us on WhatsApp first
              </a>
            </div>
            <p className="mt-3 text-center text-xs text-slate-500">
              No card needed to see how it works · 850+ UK businesses already on board
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitIntentModal;
